import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { formatDateInTimezone } from '../unified-alarm-group/controls/utils';
dayjs.extend(utc);
dayjs.extend(timezone);

export const QUICK_OPTIONS = [
  { id: '15', label: '15 min', minutes: 15 },
  { id: '30', label: '30 min', minutes: 30 },
  { id: '45', label: '45 min', minutes: 45 },
  { id: '60', label: '1 hour', minutes: 60 },
];

export interface DisarmWindowFormValue {
  startTime: string;
  endTime: string;
  quickDuration: string | null;
  activeInput: 'custom' | 'quick' | null;
}

interface TimestampRange {
  startTimestamp: number;
  endTimestamp: number;
}

export function convertDateStringToTimestamp(
  dateString: string,
  tz: string
): number {
  return dayjs.tz(dateString, tz).unix();
}

export function isDisarmWindowRelevant(endTimestamp?: number): boolean {
  return !!endTimestamp && endTimestamp * 1000 >= Date.now();
}

export function validateDisarmWindow(payload: {
  startTimestamp: number;
  endTimestamp: number;
}): { isValid: boolean; message: string } {
  if (payload.startTimestamp >= payload.endTimestamp) {
    return {
      isValid: false,
      message: 'End time should not be same or before start time',
    };
  }
  if (payload.endTimestamp * 1000 < Date.now()) {
    return { isValid: false, message: 'End time should not be in the past' };
  }
  return { isValid: true, message: '' };
}

export const getTimeWindow = (
  formValue: DisarmWindowFormValue,
  tz: string
): TimestampRange => {
  const now = dayjs().tz(tz);

  if (formValue.activeInput === 'quick' && formValue.quickDuration) {
    return {
      startTimestamp: now.unix(),
      endTimestamp: now.add(Number(formValue.quickDuration), 'minute').unix(),
    };
  }

  if (
    formValue.activeInput === 'custom' &&
    formValue.startTime &&
    formValue.endTime
  ) {
    return {
      startTimestamp: convertDateStringToTimestamp(formValue.startTime, tz),
      endTimestamp: convertDateStringToTimestamp(formValue.endTime, tz),
    };
  }

  return { startTimestamp: 0, endTimestamp: 0 };
};

export const getSummaryText = (
  formValue: DisarmWindowFormValue,
  tz: string,
  window: TimestampRange,
  isArming?: boolean
): string => {
  const startFormatted = formatDateInTimezone(window.startTimestamp, tz);
  const endFormatted = formatDateInTimezone(window.endTimestamp, tz);
  const action = isArming ? 'Arm' : 'Disarmed';

  if (formValue.activeInput === 'quick' && formValue.quickDuration) {
    return `${action} for next ${formValue.quickDuration} min\n${startFormatted} - ${endFormatted}`;
  }

  return `${action} from ${startFormatted} to ${endFormatted}`;
};

export const getInitialFormValue = (
  tz: string,
  existing?: { startTime: number; endTime: number; timezone?: string }
): DisarmWindowFormValue => {
  if (existing?.startTime && existing?.endTime) {
    const durationSeconds = existing.endTime - existing.startTime;
    const matched = QUICK_OPTIONS.find(
      (opt) => opt.minutes * 60 === durationSeconds
    );

    if (matched) {
      return {
        startTime: '',
        endTime: '',
        quickDuration: matched.id,
        activeInput: 'quick',
      };
    }

    const tzToUse = existing.timezone || tz;
    return {
      startTime: dayjs
        .unix(existing.startTime)
        .tz(tzToUse)
        .format('YYYY-MM-DDTHH:mm'),
      endTime: dayjs
        .unix(existing.endTime)
        .tz(tzToUse)
        .format('YYYY-MM-DDTHH:mm'),
      quickDuration: null,
      activeInput: 'custom',
    };
  }

  return { startTime: '', endTime: '', quickDuration: null, activeInput: null };
};
